import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { DueNow } from "@/components/Layout/DueNow/DueNow";
import {
  useBillingPeriod,
  useGetFlatPricesAndName,
  useGetIconForDevice,
  useHasVoiceOption,
} from "@/hooks/useDataContextHooks";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { DataContext } from "@/provider/DataContextProvider";
import { BasketEntry } from "@/services/model";
import { getCreditAmount } from "@/utils/basketUtils";
import { getEntry } from "@/utils/dataContextHelpers";
import { getCurrencyFromLookupValue } from "@/utils/translationHelpers";

/** Renders the payment due now box of the checkout page */
export const OnboardingDueNow: FCC = () => {
  const { dataContext } = useContext(DataContext);

  const { t } = useTranslation();
  const currency = getCurrencyFromLookupValue(
    dataContext.basket?.currency || { id: 1 },
  );

  const hasVoiceOption = useHasVoiceOption();

  const entry = getEntry(dataContext) as BasketEntry;
  const isESim = entry?.esim;

  // flat name and flat price
  const {
    flatName,
    flatPrice,
    voicePrice,
    onceTotal,
    onceSubtotal,
    discountAmount,
  } = useGetFlatPricesAndName();
  const { campaignEndDate } = useGetCampaignEndDate(dataContext.basket);

  const credit = getCreditAmount(dataContext.basket?.balance);
  const creditTimeRemaining =
    dataContext.basket?.total?.oneTime?.amountDiscounts || 0;

  const billingPeriod = useBillingPeriod();
  const billingPeriodText = t(
    "Onboarding.sections.checkout.summary.billingPeriod",
    {
      days: billingPeriod,
    },
  );

  const icon = useGetIconForDevice();

  return (
    <DueNow
      icon={icon}
      billingPeriod={billingPeriodText}
      dataRateName={flatName}
      // TODO: add specific lineItem for FreeTrial discount
      dataRatePrice={flatPrice}
      currency={currency}
      totalOnce={onceTotal - credit}
      subtotalOnce={onceSubtotal}
      discountAmount={discountAmount}
      discountEndDate={campaignEndDate}
      credit={credit}
      creditTimeRemaining={creditTimeRemaining}
      isPrecharged={!isESim}
      {...(hasVoiceOption
        ? {
            voiceOptionName: t(
              "Onboarding.sections.checkout.summary.voiceOption",
            ),
            voiceOptionPrice: onceTotal <= 0 ? 0 : voicePrice,
          }
        : {})}
    />
  );
};
